import { gql } from '@eti/schema-types';

export const paymentMethodPricesFragment = gql(/* GraphQL */ `
  fragment PaymentMethodPrices on PaymentMethodPrice {
    name
    price {
      value
    }
    type
  }
`);

export const travelerPricesFragment = gql(/* GraphQL */ `
  fragment TravelerPrices on TravelerPrice {
    id
    price {
      markup {
        value
      }
      price {
        value
        currency {
          code
        }
      }
      vat {
        value
      }
    }
    taxesAndFees {
      category
      taxes {
        code
        title
        amount {
          value
        }
      }
    }
    travelerId
  }
`);

export const availableExtraProductsFragment = gql(/* GraphQL */ `
  fragment AvailableExtraProducts on ExtraProduct {
    configuration {
      productPreSelection
    }
    productId: id
    name
    selectedWithTrip
    sellSpecification {
      ... on SellSpecificationTravelerNew {
        sellPriceTravelers {
          price {
            price {
              value
            }
            markup {
              value
            }
            vat {
              value
            }
          }
        }
      }
      ... on SellSpecificationNoneNew {
        price {
          price {
            value
            currency {
              code
              exponent
            }
          }
        }
      }
      ... on SellSpecificationBaggageNew {
        sellPriceBaggage {
          maxWeight
          numberOfUnits
          weightUnit
          price {
            price {
              value
              currency {
                code
              }
            }
          }
        }
      }
      ... on SellCabinBaggageNew {
        weight
        weightUnit
        pieces
        size3d {
          height
          length
          width
        }
        sellPriceTravelers {
          travelerId
          price {
            markup {
              value
              currency {
                code
                exponent
                name
              }
            }
            price {
              value
              currency {
                code
                exponent
                name
              }
            }
            vat {
              currency {
                code
                exponent
                name
              }
              value
            }
          }
        }
      }
    }
  }
`);

export const flightsFragment = gql(/* GraphQL */ `
  fragment Flights on Trip {
    id
    tripId
    isVI
    includedCabinBaggage {
      includedPersonalItem
      size3d {
        height
        length
        width
      }
      ...IncludedCabinBaggage
    }
    includedCheckedBaggage {
      ...IncludedCheckedBaggage
    }
    bounds {
      boundId: id
      includedCabinBaggage {
        ...IncludedCabinBaggage
      }
      includedCheckedBaggage {
        ...IncludedCheckedBaggage
      }
      segments {
        ... on TripSegment {
          __typename
          segmentId: id
          aircraftType
          arrivedAt
          brandedFareInformation {
            name
          }
          includedCabinBaggage {
            ...IncludedCabinBaggage
          }
          includedCheckedBaggage {
            ...IncludedCheckedBaggage
          }
          brandedFareInformation {
            name
          }
          cabinClassName
          departuredAt
          destination {
            code
            name
            cityCode
            cityName
            airportCode
            airportName
          }
          duration
          equipmentCode
          flightNumber
          marketingCarrier {
            code
            name
          }
          ticketingCarrier {
            code
            name
          }
          numberOfTechnicalStops
          technicalStops {
            arrivalAt
            departureAt
            duration
            location {
              code
              name
              cityName
              cityCode
            }
          }
          operatingCarrier {
            code
            name
          }
          operatingInformation
          origin {
            code
            name
            cityCode
            cityName
            airportCode
            airportName
          }
          segmentDetails {
            paxType
            numberOfSeatsLeft
          }
        }
        ... on EventSegment {
          __typename
          segmentId: id
          types
          duration
        }
      }
    }
    brandedFares {
      name
    }
    availableExtraProducts {
      ...AvailableExtraProducts
    }
    includedExtraProducts {
      id
      texts {
        name
        productSummaryAlternativeName
        readMoreText
        receiptText
        salesAbstract
      }
    }
    selectionKey
    type
    tripCharacteristics
    tripTravelers: travelers {
      id
      ageType
    }
    paymentMethodPrices {
      ...PaymentMethodPrices
    }
    travelerPrices {
      ...TravelerPrices
    }
    travelerPricesWithoutPaymentDiscounts {
      price {
        markup {
          value
        }
        price {
          value
          currency {
            code
          }
        }
        vat {
          value
        }
      }
      taxesAndFees {
        category
        taxes {
          code
          title
          amount {
            value
          }
        }
      }
      travelerId
    }
    tripTags
    systems
    voucherAmount {
      value
    }
    shareableUrl
  }
`);

export const GET_ANCILLARY_BUNDLE = gql(/* GraphQL */ `
  query getAncillaryBundle($selectionKey: String) {
    ancillaryBundle(tripKey: $selectionKey) {
      name
      id
      options {
        id
        name
        price {
          price {
            value
          }
        }
        content {
          name
          description
          readMoreText
          included
          termsUrl
          size3d {
            height
            width
            length
          }
          weight
          weightUnit
          pieces
          productTypeId
        }
        tier
        originalPrice {
          price {
            value
          }
        }
      }
      discountPercentage
    }
  }
`);

export const SEARCH_QUERY = gql(/* GraphQL */ `
  query SearchOnResultPage(
    $routes: [Route!]!
    $cabinClass: CabinClass
    $direct: Boolean
    $carrierCodes: [String]
    $adults: Int!
    $childAges: [Int]
    $offset: Int
    $sortTypeCode: String
    $filters: [Filter!]
    $validWithVoucher: Boolean
  ) {
    search(
      routes: $routes
      cabinClass: $cabinClass
      direct: $direct
      carrierCodes: $carrierCodes
      adults: $adults
      childAges: $childAges
      offset: $offset
      sortTypeCode: $sortTypeCode
      filters: $filters
      validWithVoucher: $validWithVoucher
    ) {
      availableFilters {
        code
        group
        label
        ... on RangeFilter {
          __typename
          code
          group
          label
          max
          min
          type
        }
        ... on SelectionFilter {
          __typename
          code
          group
          label
          isMultiChoice
          options {
            label
            value
          }
          type
        }
      }
      availableSortTypes {
        code
        name
      }
      carrierCodes
      carrierNames
      flights {
        ...Flights
      }
      flightsCount
      filteredFlightsCount
      quickSortPrices {
        carrierPromo {
          value
          currency {
            code
          }
        }
        cheapTrip {
          value
          currency {
            code
          }
        }
        shortTrip {
          value
          currency {
            code
          }
        }
        recommendation {
          value
          currency {
            code
          }
        }
        bestValue {
          value
          currency {
            code
          }
        }
        longStay {
          value
          currency {
            code
          }
        }
      }
      resultSetMetaData {
        priceRange {
          min
          max
        }
        travelTimeRange {
          min
          max
        }
        marketingCarriers {
          code
          name
        }
      }
      routes {
        origin {
          code
          name
          cityCode
          cityName
          countryCode
          countryName
          continentCode
          continentName
        }
        destination {
          code
          name
          cityCode
          cityName
          countryCode
          countryName
          continentCode
          continentName
        }
        departureAt
        departureDate
        departureTimeOfDay
      }
      searchPath
      sponsoredTrips {
        ...Flights
      }
      travelers {
        ageType
      }
      type
      tripCampaigns {
        campaignImageUrlDesktop
        campaignImageUrlMobile
        selectionKey
      }
      validWithVoucher
    }
  }
`);

export const SEARCH_MORE_QUERY = gql(/* GraphQL */ `
  query SearchMoreOnResultPage(
    $routes: [Route!]!
    $cabinClass: CabinClass
    $direct: Boolean
    $carrierCodes: [String]
    $adults: Int!
    $childAges: [Int]
    $offset: Int
    $sortTypeCode: String
    $filters: [Filter!]
    $validWithVoucher: Boolean
  ) {
    search(
      routes: $routes
      cabinClass: $cabinClass
      direct: $direct
      carrierCodes: $carrierCodes
      adults: $adults
      childAges: $childAges
      offset: $offset
      sortTypeCode: $sortTypeCode
      filters: $filters
      validWithVoucher: $validWithVoucher
    ) {
      flights {
        ...Flights
      }
    }
  }
`);
